import { enhancedScopeDiffData$ } from 'scope/enhancedScopeDiffData$';
import styled from 'styled-components';
import { connect } from '@ardoq/rxbeach';
import { combineLatest } from 'rxjs';
import {
  APIDiffBranchToBranch,
  APIEntityType,
  APIOrganizationUser,
  ArdoqId,
} from '@ardoq/api-types';
import { BasicTable } from '@ardoq/table-legacy';
import {
  EnhancedScopeDataWithBranchName,
  FieldType,
  RenderOption,
  RenderOptions,
  Renderer,
  graphics,
} from '@ardoq/renderers';
import { map } from 'rxjs/operators';
import { orgUsers$ } from 'streams/orgUsers/orgUsers$';
import { t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';

const renderOptions: RenderOptions = {
  definitions: {
    [RenderOption.TEXT_ELLIPSIS]: {
      maxLength: 180,
    },
  },
  types: {
    [FieldType.DESCRIPTION]: new Set([RenderOption.TEXT_ELLIPSIS]),
  },
};

const { Table, Td, Tbody, Th, Thead } = BasicTable;

const StyledTh = styled(Th)`
  background: silver;

  &:hover {
    background: silver;
  }
`;

type ChangedPopoverContentProps = {
  entityId: ArdoqId;
  main: EnhancedScopeDataWithBranchName | null;
  branch: EnhancedScopeDataWithBranchName | null;
  branchOff: EnhancedScopeDataWithBranchName | null;
  diffData: APIDiffBranchToBranch | null;
  users: Record<ArdoqId, APIOrganizationUser>;
  i18nLocale: I18nLocale;
};

const ChangedPopoverContent = ({
  entityId,
  main,
  branch,
  branchOff,
  diffData,
  users,
  i18nLocale,
}: ChangedPopoverContentProps) => {
  if (!(diffData && main && branch && branchOff)) {
    return null;
  }

  const componentDiff = diffData.components.update?.[entityId];
  const referenceDiff = diffData.references.update?.[entityId];
  const diff = componentDiff ?? referenceDiff;

  if (!diff) {
    return null;
  }

  const [entityType, headerFieldName] = componentDiff
    ? [APIEntityType.COMPONENT, 'name']
    : [APIEntityType.REFERENCE, 'type'];

  return (
    <div>
      <Table>
        <Thead>
          <tr>
            <StyledTh colSpan={4}>
              <Renderer
                entityType={entityType}
                entityId={entityId}
                fieldName={headerFieldName}
                enhancedScopeData={branch}
                users={users}
                graphics={graphics}
                options={renderOptions}
                i18nLocale={i18nLocale}
              />
            </StyledTh>
          </tr>
        </Thead>
        <Tbody>
          <tr>
            <Td></Td>
            <Td>
              {t(
                i18nLocale,
                'scope:changePopover.originalValue',
                'Original value'
              )}
            </Td>
            <Td>
              {t(
                i18nLocale,
                'scope:changePopover.mainlineValue',
                'Mainline value'
              )}
            </Td>
            <Td>
              {t(
                i18nLocale,
                'scope:changePopover.scenarioValue',
                'Scenario value'
              )}
            </Td>
          </tr>
          {Object.keys(diff).map(fieldName => (
            <tr key={fieldName}>
              <Td>{fieldName}</Td>
              <Td>
                <Renderer
                  entityType={entityType}
                  entityId={entityId}
                  fieldName={fieldName}
                  enhancedScopeData={branchOff}
                  users={users}
                  graphics={graphics}
                  options={renderOptions}
                  i18nLocale={i18nLocale}
                />
              </Td>
              <Td>
                <Renderer
                  entityType={entityType}
                  entityId={entityId}
                  fieldName={fieldName}
                  enhancedScopeData={main}
                  users={users}
                  graphics={graphics}
                  options={renderOptions}
                  i18nLocale={i18nLocale}
                />
              </Td>
              <Td>
                <Renderer
                  entityType={entityType}
                  entityId={entityId}
                  fieldName={fieldName}
                  enhancedScopeData={branch}
                  users={users}
                  graphics={graphics}
                  options={renderOptions}
                  i18nLocale={i18nLocale}
                />
              </Td>
            </tr>
          ))}
        </Tbody>
      </Table>
    </div>
  );
};

const ChangePopoverStream$ = combineLatest([
  orgUsers$,
  enhancedScopeDiffData$,
  i18nLocale$,
]).pipe(
  map(
    ([{ byId: users }, { diffData, main, branch, branchOff }, i18nLocale]) => ({
      users,
      diffData,
      main,
      branch,
      branchOff,
      i18nLocale,
    })
  )
);

export const ChangePopoverContent = connect(
  ChangedPopoverContent,
  ChangePopoverStream$
);
